import React from "react";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  LegendItem,
  PieController
} from "chart.js";


import { Avatar, Box, Card, CardContent, Typography } from "@mui/material";
import { Favorite } from "@mui/icons-material";

Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PieController
);

const StatisticsPieChart = ({
  data,
  title,
  type,
}: {
  data: any[];
  title: string;
  type: string;
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<Chart | null>(null);

  React.useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      if (ctx) {
        // Destroy existing chart if it exists
        if (chartRef.current) {
          chartRef.current.destroy();
        }

        chartRef.current = new Chart(ctx, {
          type: "pie",
          data: {
            labels: data.map((item) => item.name),
            datasets: [
              {
                data: data.map((item) => item.count_num),
                backgroundColor: [
                  "#4CAF50", // Green for บทความ
                  "#FF6B6B", // Red for ข่าวเตือนภัย (largest segment)
                  "#BA68C8", // Purple for วารสาร
                  "#D2B48C", // Tan for สารคดีเกษตร
                ],
                borderColor: ["#4CAF50", "#FF6B6B", "#BA68C8", "#D2B48C"],
                borderWidth: 2,
                hoverBorderWidth: 3,
                hoverOffset: 10,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              // title: {
              //   display: true,
              //   text: title,
              //   font: {
              //     size: 24,
              //     weight: "bold",
              //   },
              //   padding: {
              //     top: 20,
              //     bottom: 30,
              //   },
              // },
              legend: {
                position: "bottom",
                labels: {
                  font: {
                    size: 12,
                  },
                  usePointStyle: true,
                  pointStyle: "circle",
                  generateLabels: function (chart) {
                    const data = chart.data;
                    if (data.labels && data.datasets.length) {
                      return data.labels.map((label, i) => {
                        const dataset = data.datasets[0];
                        const numbers = dataset.data.filter(
                          (value): value is number => typeof value === "number"
                        );
                        const total = numbers.reduce((a, b) => a + b, 0);
                        const count = Number(dataset.data[i]) || 0;
                        const percent = total > 0
                          ? ((count / total) * 100).toFixed(2)
                          : "0.00";
                        return {
                          text: `${label}: ${count.toLocaleString()}${type === "statistics_filter" ? ` (${percent || 0}%)` : ""
                            }`,

                          fillStyle: Array.isArray(dataset.backgroundColor)
                            ? (dataset.backgroundColor[i] as string)
                            : undefined,
                          strokeStyle: Array.isArray(dataset.borderColor)
                            ? (dataset.borderColor[i] as string)
                            : undefined,
                          lineWidth:
                            typeof dataset.borderWidth === "number"
                              ? dataset.borderWidth
                              : 1,
                          hidden: false,
                          index: i,
                        } as LegendItem;
                      });
                    }
                    return [];
                  },
                },
              },
              tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleColor: "white",
                bodyColor: "white",
                borderColor: "rgba(255, 255, 255, 0.3)",
                borderWidth: 1,
                cornerRadius: 8,
                callbacks: {
                  label: function (context) {
                    const label = context.label || "";
                    const value = context.parsed as number;
                    const total = (context.dataset.data as number[]).reduce(
                      (a, b) => a + b,
                      0
                    );
                    const percentage = ((value / total) * 100).toFixed(2);
                    return [
                      `${label}`,
                      `จำนวน: ${value.toLocaleString()}`,
                      `เปอร์เซ็นต์: ${percentage || 0}%`,
                    ];
                  },
                },
              },
            },
            animation: {
              animateRotate: true,
              animateScale: true,
              duration: 1500,
            },
          },
        });
      }
    }

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [data]);

  return (
    <Card sx={{ width: "100%", maxWidth: "500px", maxHeight: "550px", minHeight: "550px" }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          {title}
        </Typography>

        {type === "sum_like" && (
          <>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                padding: 1,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                maxWidth: "fit-content",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#e91e63",
                  width: 32,
                  height: 32,
                }}
              >
                <Favorite sx={{ color: "white", fontSize: 18 }} />
              </Avatar>

              <Typography
                variant="body1"
                component="div"
                sx={{
                  fontWeight: "medium",
                  color: "#333",
                }}
              >
                {data?.reduce((x, y) => x + Number(y?.count_num || 0), 0)}
              </Typography>
            </Box>
          </>
        )}
        <br />

        <Box sx={{ height: 400, minHeight: 400, maxHeight: 400 }}>
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatisticsPieChart;